// src/pages/BlogPost.js
import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import blogArticles from "../data/blogArticles";
import "./Blog.css";
import useSEO from "../hooks/useSEO";

function formatDate(dateStr) {
  return new Date(dateStr).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

// Convertit un markdown simple en JSX, bloc par bloc (blocs séparés par une ligne vide) :
// ## titre, ### sous-titre, listes "- " et "1. ", blocs de code ```, paragraphes,
// et dans le texte : [lien](/chemin) et `code`.
// Même logique que renderMarkdown() dans scripts/prerender.mjs : modifier les deux ensemble.
const INLINE = /\[([^\]]+)\]\(([^)\s]+)\)|`([^`]+)`/g;

function renderInline(text, keyPrefix) {
  const out = [];
  let last = 0;
  let m;
  INLINE.lastIndex = 0;
  while ((m = INLINE.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyPrefix}-${m.index}`;
    if (m[3] !== undefined) {
      out.push(<code key={key}>{m[3]}</code>);
    } else if (m[2].startsWith("/")) {
      out.push(<Link key={key} to={m[2]}>{m[1]}</Link>);
    } else {
      out.push(<a key={key} href={m[2]} target="_blank" rel="noopener noreferrer">{m[1]}</a>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

function renderContent(content) {
  const blocks = content.trim().split("\n\n");

  return blocks.map((block, i) => {
    const trimmed = block.trim();

    if (trimmed.startsWith("```")) {
      const code = trimmed.replace(/^```[^\n]*\n?/, "").replace(/\n?```$/, "");
      return <pre key={i}><code>{code}</code></pre>;
    }

    if (trimmed.startsWith("### ")) {
      return <h3 key={i}>{renderInline(trimmed.slice(4), i)}</h3>;
    }

    if (trimmed.startsWith("## ")) {
      return <h2 key={i}>{renderInline(trimmed.slice(3), i)}</h2>;
    }

    if (trimmed.startsWith("- ")) {
      const items = trimmed.split("\n").map((line) => line.trim().replace(/^- /, ""));
      return (
        <ul key={i}>
          {items.map((item, j) => (
            <li key={j}>{renderInline(item, `${i}-${j}`)}</li>
          ))}
        </ul>
      );
    }

    if (/^\d+\. /.test(trimmed)) {
      const items = trimmed.split("\n").map((line) => line.trim().replace(/^\d+\. /, ""));
      return (
        <ol key={i}>
          {items.map((item, j) => (
            <li key={j}>{renderInline(item, `${i}-${j}`)}</li>
          ))}
        </ol>
      );
    }

    return <p key={i}>{renderInline(trimmed, i)}</p>;
  });
}

export default function BlogPost() {
  const { slug } = useParams();
  const article = blogArticles.find((a) => a.slug === slug);
  useSEO(`/blog/${slug}`, {
    title: article ? `${article.title} | CyberGuard` : undefined,
    description: article ? article.excerpt : undefined,
  });

  if (!article) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="blog-post-page">
      <Link to="/blog" className="blog-back-link">
        ← Retour au blog
      </Link>

      <article>
        <span className="blog-card-category">{article.category}</span>
        <h1>{article.title}</h1>
        <div className="blog-card-meta">
          <span>{article.author}</span>
          <span>·</span>
          <span>{formatDate(article.date)}</span>
          <span>·</span>
          <span>{article.readingTime} de lecture</span>
        </div>

        <div className="blog-post-content">{renderContent(article.content)}</div>
      </article>
    </div>
  );
}
